import { useMutation } from '@tanstack/react-query';
import { App } from 'antd';

import { isAuthenticated } from '@/shared/api/storage';
import { useStore } from '@/shared/store/app';
import { useUserInfo } from '@/features/auth/hooks/useUserInfo';
import { ChangeAppSender } from '@/shared/lib/change-app';
import i18n from '@/shared/i18n';

interface UseChangeAppOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface ChangeAppTarget {
  /** Target URL to navigate to */
  url: string;
  /**
   * IAM OAuth client ID for the target portal.
   * When omitted the URL is opened externally via `window.open`.
   */
  targetClientId?: string;
}

/**
 * useChangeApp
 *
 * Cross-portal navigation hook via IAM change-app flow.
 *
 * ## Features
 * - Resolves target URLs to known portal client IDs
 * - Obtains minitoken from IAM and redirects to target portal
 * - Falls back to `window.open` for unrecognized URLs
 * - Displays error notifications via global store on failure
 *
 * @param options - Optional callbacks for success and error handling
 * @param options.onSuccess - Called after successful navigation
 * @param options.onError - Called with the error when navigation fails
 *
 * @returns TanStack Query mutation object
 * @returns returns.mutate - Trigger navigation to a URL
 * @returns returns.isPending - Whether navigation is in progress
 * @returns returns.isError - Whether the last navigation failed
 * @returns returns.error - Error from the last failed navigation
 *
 * @example
 * ```tsx
 * const { mutate: navigateTo, isPending } = useChangeApp();
 *
 * // Known portal — IAM change-app flow
 * navigateTo({ url: 'https://plataformadatatrust.clearsale.com.br/consulta', targetClientId: 'pdt-client-id' });
 *
 * // Unknown URL — opens in new tab
 * navigateTo({ url: 'https://docs.experian.com' });
 * ```
 *
 * @since 1.0.0
 * @category Hooks
 */
export function useChangeApp(options?: UseChangeAppOptions) {
  const { notification } = App.useApp();
  const { setIsNavigating } = useStore();
  const { data: userInfo } = useUserInfo();

  return useMutation<void, Error, ChangeAppTarget>({
    mutationFn: async ({ url, targetClientId }) => {
      if (!targetClientId) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }

      if (!isAuthenticated()) {
        throw new Error(
          i18n.t('Your session has expired, please log in again')
        );
      }

      if (!userInfo?.userId) {
        throw new Error(
          i18n.t('User information is not available, please try again')
        );
      }

      const sender = new ChangeAppSender({
        bffBaseUrl: import.meta.env?.VITE_API_BASE_URL ?? '',
      });

      await sender.navigateTo({
        targetClientId,
        targetUrl: url,
        originClientId: import.meta.env.VITE_SSO_CLIENT_ID,
        userId: userInfo.userId,
      });
    },
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onMutate: ({ targetClientId }) => {
      if (targetClientId) setIsNavigating(true);
    },
    onSettled: () => {
      setIsNavigating(false);
    },
    onError: (error) => {
      const message = i18n.t(
        'Failed to navigate to the application. Please try again.'
      );
      notification.error({ message });
      options?.onError?.(error);
    },
  });
}
